import { isUpcomingPeriod, useReviewPeriodLock, useReviewPeriodLocked, periodQuery } from '../workspace/reviewPeriod';
import { useSearchParams } from 'react-router-dom';
import { CollectionsDrawer } from '../collections/CollectionsExperience';
import { WorkspaceGuide, WorkspaceTools } from '../components/WorkspaceExperience';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, Check, CircleDot } from 'lucide-react';
import axios from 'axios';
import { apiErrorMessage, refreshMonitoringEvidence } from '../api/abfApi';
import { useAuth } from '../../../contexts/AuthContext';
import LegacyRetirementReport from './LegacyRetirementReport';
import { type ActionFeedbackState } from '../components/ActionFeedback';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { fmtDate, fmtMoney, fmtNumber } from '../presentation/format';
import { certificateLineUnit, certificateUnitWord, certificateValueText, type CertificateLine } from './certificateLines';
import { Button, Card, CardHead, INK, fieldBase } from '../onboarding/review/reviewStyles';
import { Layout, Note, Readable, Signoff, SmallButton, TaskButton, TaskRail, Work } from '../onboarding/review/contractReviewStyles';
import {
  CertificateForm,
  ExceptionActions,
  ThresholdDecision,
  ThresholdForm,
  WaiverDecision,
  WaiverRequestForm,
} from './MonitoringActions';

// ---------------------------------------------------------------------------
// Review standard, same shell as Period review and Funding.
// ---------------------------------------------------------------------------

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.55; margin-top: 14px;
  h2, h3, p { margin: 0; } h2 { font-size: 17px; } h3 { font-size: 15px; }
  button, input, select, textarea { font-family: inherit; }
`;

const Journey = styled.ol`
  display: grid; grid-template-columns: repeat(3, 1fr); list-style: none;
  padding: 0; margin: 0 0 18px; border: 1px solid ${INK.line}; border-radius: 10px; overflow: hidden;
  li { display: flex; gap: 12px; align-items: center; padding: 13px 16px; background: #0c1626; }
  li + li { border-left: 1px solid ${INK.line}; }
  li[data-current='true'] { background: #12273c; box-shadow: inset 0 -2px ${INK.accent}; }
  .step-icon { display: grid; place-items: center; width: 28px; height: 28px; border: 1px solid ${INK.lineStrong}; border-radius: 50%; color: #b5c9dd; flex-shrink: 0; }
  strong { display: block; font-size: 14px; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 2px; }
  @media (max-width: 700px) { grid-template-columns: 1fr; li + li { border-left: 0; border-top: 1px solid ${INK.line}; } }
`;

const Strip = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; margin-bottom: 8px;
  > div { padding: 12px 14px; border: 1px solid ${INK.line}; border-radius: 10px; background: ${INK.panel}; }
  > div[data-tone='bad'] { border-color: rgba(240,162,74,.5); background: #1d1710; }
  span { display: block; color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
  strong { display: block; margin-top: 4px; font-size: 18px; color: ${INK.strong}; font-variant-numeric: tabular-nums; }
  strong[data-tone='bad'] { color: #fca5a5; } strong[data-tone='warn'] { color: #edcc82; } strong[data-tone='good'] { color: #7cddbe; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 4px; }
`;

const StageLine = styled.ol`
  display: flex; gap: 4px; list-style: none; padding: 0; margin: 6px 0 0;
  li { width: 14px; height: 6px; border-radius: 3px; background: #26364a; }
  li[data-state='DONE'] { background: #4fd1a5; }
  li[data-state='OPEN'] { background: ${INK.accent}; }
  li[data-state='BLOCKED'] { background: #f0a24a; }
`;

const Attention = styled.article`
  padding: 12px 14px; border: 1px solid rgba(240,162,74,.5); border-radius: 10px;
  background: ${INK.panel}; margin-bottom: 10px; display: flex; gap: 10px; align-items: flex-start;
  strong { color: ${INK.strong}; } p { color: ${INK.muted}; font-size: 13px; margin-top: 3px; }
  svg { color: #f0a24a; flex-shrink: 0; margin-top: 2px; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid ${INK.line}; vertical-align: top; }
  th { color: ${INK.faint}; font-size: 11px; font-weight: 750; letter-spacing: .06em; text-transform: uppercase; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr[data-breach='true'] td:first-child { border-left: 3px solid #f0a24a; }
  small { display: block; color: ${INK.faint}; font-size: 11px; }
`;

const Trend = styled.span`
  display: inline-flex; gap: 3px; align-items: flex-end; height: 16px;
  i { display: block; width: 6px; border-radius: 1px; background: #4fd1a5; }
  i[data-state='BREACH'] { background: #f0a24a; }
  i[data-state='WAIVED'] { background: #a78bfa; }
`;

const Facts = styled.dl`
  display: grid; grid-template-columns: max-content 1fr; gap: 6px 14px; margin: 0;
  dt { color: ${INK.muted}; font-size: 13px; }
  dd { margin: 0; color: ${INK.body}; font-size: 13px; overflow-wrap: anywhere; }
`;

const Grid2 = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; align-items: start; margin-top: 14px;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;



const Bar = styled.div`
  position: sticky; bottom: 0; z-index: 5; display: flex; flex-wrap: wrap; align-items: center;
  justify-content: space-between; gap: 12px; margin-top: 18px; padding: 12px 14px;
  border: 1px solid ${INK.line}; border-radius: 10px; background: rgba(9,18,31,.96); backdrop-filter: blur(4px);
  .state { color: ${INK.muted}; font-size: 13px; } .state strong { color: ${INK.strong}; }
  .actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  input { ${fieldBase} width: 240px; }
`;

// ---------------------------------------------------------------------------

const ROOT = '/api/credit/abf/round2';
// The shared money and number styles (presentation/format.ts); the panel's models are untyped maps, hence the casts.
const money = (v: unknown, c = 'USD') => fmtMoney(v as number | string | null | undefined, c);
const num = (v: unknown, digits = 2) => fmtNumber(v as number | string | null | undefined, digits);
const date = (v: unknown) => fmtDate(v == null ? null : String(v));
const who = (v: unknown) => v ? String(v) : '—';
const plain = (v: unknown, fb = '—') => v == null || v === '' ? fb : String(v).replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());

/** A covenant threshold reads as the agreement states it, not as an enum. */
const thresholdText = (c: any) => c.threshold_text
  || `${c.threshold_type === 'MIN' ? 'at least' : 'no more than'} ${isMoney(c.covenant_key) ? money(c.threshold_value) : `${num(c.threshold_value)}%`}`;
const isMoney = (key: string) => String(key).includes('AVAILABILITY');
const valueText = (c: any, v: unknown) => isMoney(c.covenant_key) ? money(v) : `${num(v)}%`;

const stateTone = (state?: string): StatusTone =>
  state === 'BREACH' ? 'critical' : state === 'WAIVED' || state === 'WARNING' ? 'warning'
    : state === 'INDETERMINATE' ? 'neutral' : state ? 'done' : 'neutral';
const stateWord = (state?: string) =>
  state === 'BREACH' ? 'In breach' : state === 'WAIVED' ? 'Waived' : state === 'CURED' ? 'Cured'
    : state === 'WARNING' ? 'At risk'
    : state === 'INDETERMINATE' ? 'Cannot be measured' : state === 'PASS' ? 'Passing' : 'Not tested';
const headroomWord = (pct?: number | null) =>
  pct == null ? '' : `${pct < 0 ? '' : '+'}${Number(pct).toFixed(1)}% of threshold`;

/** Headroom of one trend point, as a percentage of its own threshold. */
const headroomOf = (t: any): number | null => {
  const cushion = Number(t?.signed_cushion);
  const threshold = Math.abs(Number(t?.threshold_value));
  if (!Number.isFinite(cushion) || !Number.isFinite(threshold) || threshold === 0) return null;
  return (cushion / threshold) * 100;
};

/**
 * The bar used to encode only the sign of the cushion, so a covenant with 1% of room and
 * one with 60% drew the same shape and the sparkline said nothing a colour did not already
 * say. Height now tracks headroom: a breach draws short, comfortable headroom draws tall,
 * and 20% of threshold is the point where the bar reaches full height.
 */
const barHeight = (t: any): number => {
  const pct = headroomOf(t);
  if (pct == null) return 4;
  const clamped = Math.max(-10, Math.min(20, pct));
  return Math.round(3 + ((clamped + 10) / 30) * 13);
};

interface Props {
  facilityId: number;
  currency?: string | null;
  /** The active contract; lets a run be named "contract version 2" instead of by its row id. */
  contractVersion?: { recordId: number | null; versionNumber: number | null };
  onFeedback: (feedback: ActionFeedbackState) => void;
}

/** A covenant run records the contract version's row id; only the active version's number is known here. */
const contractVersionLabel = (facilityVersionId: unknown, active?: { recordId: number | null; versionNumber: number | null }) => {
  if (facilityVersionId == null || facilityVersionId === '') return 'the active contract';
  const id = Number(facilityVersionId);
  if (active && active.recordId != null && active.versionNumber != null && Number(active.recordId) === id) {
    return `contract version ${active.versionNumber}`;
  }
  return `contract record #${String(facilityVersionId)}`;
};

const CovenantMonitoringPanel: React.FC<Props> = ({ facilityId, currency, contractVersion, onFeedback }) => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const requestedPeriod = Number(params.get('period') || params.get('periodId')) || undefined;
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [onlyAttention, setOnlyAttention] = useState(false);
  const periodChangeLocked = useReviewPeriodLocked();
  const [model, setModel] = useState<any>(null);
  const [periodId, setPeriodId] = useState<number | undefined>();
  const [busy, setBusy] = useState<string | null>(null);
  useReviewPeriodLock(busy !== null);
  const workRef = useRef<HTMLElement>(null);

  const loadSequence = useRef(0);
  useEffect(() => () => { ++loadSequence.current; }, []);
  const load = useCallback(async (id?: number) => {
    const sequence = ++loadSequence.current;
    try {
      const { data } = await axios.get(`${ROOT}/facilities/${facilityId}/monitoring-workspace`,
        { params: { periodId: id, _: Date.now() } });
      if (sequence !== loadSequence.current) return;
      setModel(data);
      setPeriodId(data.selectedPeriod?.id);
    } catch (error) {
      if (sequence !== loadSequence.current) return;
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'Unable to load covenant monitoring.') });
    }
  }, [facilityId, onFeedback]);

  useEffect(() => { void load(requestedPeriod); }, [load, requestedPeriod]);

  const run = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      await action();
      onFeedback({ tone: 'success', message: success });
      await load(periodId);
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The action was refused.') });
    } finally { setBusy(null); }
  };

  const periods = useMemo(() => model?.periods || [], [model?.periods]);
  // One open action at a time. A governed decision deserves the reader's whole attention,
  // and two half-filled forms on one screen is how the wrong record gets the right answer.
  const [action, setAction] = useState<{ kind: string; id?: number } | null>(null);
  const [thresholdMetric, setThresholdMetric] = useState<string | undefined>();
  const [retirementOpen, setRetirementOpen] = useState(false);
  const closeAction = useCallback((message?: string) => {
    setAction(null);
    if (message) onFeedback({ tone: 'success', message });
    void load(periodId);
  }, [load, onFeedback, periodId]);
  const isOpen = (kind: string, id?: number) => action?.kind === kind && action?.id === id;
  // PROD-023: the person who proposed something is not offered the decision on it. The
  // server refuses either way, but a button that always fails is a worse answer than no
  // button and a sentence. Whether separation applies at all is the tenant's policy, read
  // from the same resolver the server enforces, so this can never claim a rule that is off.
  const separationRequired = model?.policy?.value === 'REQUIRED';
  const mine = (proposer?: string) =>
    separationRequired && Boolean(proposer) && proposer === user?.username;

  const toggleThresholdForm = (metricName: string) => {
    const alreadyOpen = action?.kind === 'threshold-new' && thresholdMetric === metricName;
    setThresholdMetric(alreadyOpen ? undefined : metricName);
    setAction(alreadyOpen ? null : { kind: 'threshold-new' });
  };
  if (!model) return <Shell><Note>Loading covenant monitoring…</Note></Shell>;

  const selected = model.selectedPeriod || {};
  const covenants = model.covenants || [];
  const attention = model.attention || [];
  const consequencesActive = model.consequences?.active || [];
  const waivers = model.waivers || [];
  const exceptions = (model.exceptions || []).filter((e: any) => e.live);
  const overdue = exceptions.filter((e: any) => ['OVERDUE', 'ESCALATED'].includes(String(e.sla_status)));
  const breaches = covenants.filter((c: any) => c.result?.state === 'BREACH').length;
  const atRisk = covenants.filter((c: any) => c.result?.state === 'WARNING').length;
  // F-S4-028: a waived breach is still a breach. Counting only state === 'BREACH'
  // made a facility operating under a waiver read "All passing" beside a row showing
  // a negative cushion, which is the overclaim the FRD forbids.
  const waived = covenants.filter((c: any) => c.result?.state === 'WAIVED').length;
  const poolMetrics = model.poolMetrics || {};
  const provenance = model.provenance || {};
  // 1 tests run · 2 a breach has consequences · 3 waive it or work it
  const journeyStep: 1 | 2 | 3 = waivers.some((w: any) => w.status === 'ACTIVE') || exceptions.length > 0 ? 3
    : breaches > 0 || consequencesActive.length > 0 ? 2 : 1;

  const select = (id: number) => {
    setPeriodId(id); setReviewId(null); setAction(null); const next = periodQuery(params, id); setParams(next);
    if (window.matchMedia('(max-width: 1000px)').matches) workRef.current?.scrollIntoView({ block: 'start' });
  };

  return <Shell>
    {reviewId != null && (() => {
      const covenant = covenants.find((c: any) => c.id === reviewId);
      if (!covenant) return null;
      const result = covenant.result || {};
      const relatedWaivers = waivers.filter((w: any) => Number(w.covenant_definition_id) === Number(covenant.id));
      const relatedConsequences = (model.consequences?.all || []).filter((c: any) => Number(c.covenant_definition_id) === Number(covenant.id));
      return <CollectionsDrawer title={covenant.covenant_name} onClose={() => setReviewId(null)} width={850}>
        <WorkspaceGuide><h2>{stateWord(result.state)} · {selected.period_label}</h2><p>{covenant.source_clause || 'Source clause not recorded'}</p><p>{result.test_date ? `Tested ${date(result.test_date)}` : 'No test recorded'}{covenant.resultIsFromAnotherPeriod ? ' · Result is from an earlier period' : ''}</p></WorkspaceGuide>
        <Facts><dt>Agreement threshold</dt><dd>{thresholdText(covenant)}</dd><dt>Actual</dt><dd>{result.actual_value == null ? 'Not available' : valueText(covenant, result.actual_value)}</dd><dt>Cushion</dt><dd>{result.signed_cushion == null ? 'Not available' : valueText(covenant, result.signed_cushion)}</dd><dt>Formula</dt><dd>{covenant.formula_expression || 'Not recorded'}</dd><dt>Input authority</dt><dd>{contractVersionLabel(provenance.facilityVersionId, contractVersion)} · {provenance.inputFingerprint || 'No input fingerprint'}</dd></Facts>
        <h3 style={{marginTop:20}}>Related consequences</h3>{relatedConsequences.length ? relatedConsequences.map((c:any) => <p key={c.id}>{plain(c.consequence_type)} · {c.effect_text} · {plain(c.status)}</p>) : <p>No consequence is linked to this covenant record. Facility-wide controls remain visible in the main review.</p>}
        <h3 style={{marginTop:20}}>Waivers & expiry</h3>{relatedWaivers.length ? relatedWaivers.map((w:any) => <p key={w.id}>{plain(w.status)} · ends {date(w.effective_to)} · {plain(w.waiver_type)}</p>) : <p>No waiver is linked to this covenant record.</p>}
        {result.state === 'BREACH' && <SmallButton type="button" onClick={() => { setReviewId(null); setAction({kind:'waiver-request',id:covenant.id}); }}>Review a waiver request</SmallButton>}
      </CollectionsDrawer>;
    })()}
    <Journey aria-label="How monitoring works">
      <li data-current={journeyStep === 1}><span className="step-icon">1</span><div><strong>Tests run on their own</strong><small>On every released tape and when a period closes — nobody presses a button</small></div></li>
      <li data-current={journeyStep === 2}><span className="step-icon">2</span><div><strong>A breach has consequences</strong><small>The agreement's effect applies itself and says since when</small></div></li>
      <li data-current={journeyStep === 3}><span className="step-icon">3</span><div><strong>Waive it, or work it</strong><small>A waiver with an end date, or an exception with an owner and a due date</small></div></li>
    </Journey>

    <Strip aria-label="This period">
      <div data-tone={breaches > 0 || waived > 0 ? 'bad' : undefined}>
        <span>Covenants in breach</span>
        <strong data-tone={breaches > 0 ? 'bad' : waived > 0 ? 'warn' : 'good'}>{breaches + waived}</strong>
        <small>{waived > 0 ? `${waived} covered by a waiver · ` : ''}{atRisk > 0 ? `${atRisk} at risk · ` : ''}of {covenants.length} tested in {selected.period_label || 'this period'}</small>
      </div>
      <div data-tone={consequencesActive.length > 0 ? 'bad' : undefined}>
        <span>Consequences in force</span>
        <strong data-tone={consequencesActive.length > 0 ? 'bad' : 'good'}>{consequencesActive.length}</strong>
        <small>{consequencesActive.length ? consequencesActive[0].effect_text : 'Nothing is restricting the facility'}</small>
      </div>
      <div>
        <span>Waivers</span>
        <strong data-tone={waivers.some((w: any) => w.expiringSoon) ? 'warn' : undefined}>
          {waivers.filter((w: any) => w.status === 'ACTIVE').length}
        </strong>
        <small>{waivers.some((w: any) => w.expiringSoon) ? 'one ends within 14 days' : 'none ending soon'}</small>
      </div>
      <div data-tone={overdue.length > 0 ? 'bad' : undefined}>
        <span>Exceptions open</span>
        <strong data-tone={overdue.length > 0 ? 'bad' : undefined}>{exceptions.length}</strong>
        <small>{overdue.length} past due</small>
      </div>
      <div>
        <span>Period</span>
        <strong>{plain(selected.status, '—')}</strong>
        <small>{selected.closed_at ? `Closed by ${who(selected.closed_by)}` : `Determination ${date(selected.determination_date)}`}</small>
      </div>
    </Strip>

    {!model.coverage?.monitored && <Note style={{ marginBottom: 14 }}>
      <strong>Nothing is testing this facility's covenants.</strong>
      <p>{model.coverage?.note}</p>
    </Note>}

    {attention.map((a: any, i: number) => (
      <Attention key={`${a.kind}-${i}`}><AlertTriangle size={16} />
        <div><strong>{a.title}</strong><p>{a.detail}</p></div>
      </Attention>
    ))}

    <Layout>
      <TaskRail aria-label="Collection periods">
        <div className="rail-heading"><h2>Periods</h2><p>Covenants are tested per period</p></div>
        <div className="tasks">
          {(() => {
            // Current and past periods newest first; months that have not ended yet sit under
            // "Upcoming" at the bottom, as the Collections rail does, so the period that needs
            // work is the first thing in the list.
            const businessDate = String(model.businessDate || '').slice(0, 10);
            const isUpcoming = (p: any) => isUpcomingPeriod(String(p.period_start || ''), businessDate);
            const current = periods.filter((p: any) => !isUpcoming(p)).slice().sort((a: any, b: any) => String(b.period_start).localeCompare(String(a.period_start)));
            const later = periods.filter(isUpcoming).slice().sort((a: any, b: any) => String(a.period_start).localeCompare(String(b.period_start)));
            const renderPeriod = (p: any, upcoming: boolean) => (
              <TaskButton key={p.id} type="button" disabled={periodChangeLocked} $active={p.id === periodId} aria-pressed={p.id === periodId} onClick={() => select(p.id)}>
                <div className="category">{date(p.period_start)} – {date(p.period_end)}</div>
                <strong>{p.period_label}</strong>
                <StageLine aria-label="Progress">
                  <li data-state={p.tests > 0 ? 'DONE' : upcoming ? undefined : 'OPEN'} title="Tested" />
                  <li data-state={p.breaches > 0 ? 'BLOCKED' : p.waived > 0 ? 'ACTIVE' : p.tests > 0 ? 'DONE' : undefined} title="Breaches" />
                  <li data-state={p.status === 'CLOSED' ? 'DONE' : upcoming ? undefined : 'OPEN'} title="Closed" />
                </StageLine>
                <span className="reason" style={{ color: p.breaches > 0 || p.waived > 0 ? '#f0a24a' : p.tests > 0 ? '#7cddbe' : undefined }}>
                  {p.breaches > 0 || p.waived > 0 ? <CircleDot size={12} /> : <Check size={12} />}
                  {p.tests > 0
                    ? `${p.tests} tested${p.breaches > 0 ? `, ${p.breaches} in breach` : ''}${p.waived > 0 ? `, ${p.waived} waived` : ''}`
                    : upcoming ? 'Not started' : 'Not tested yet'}
                </span>
              </TaskButton>
            );
            return <>
              {current.map((p: any) => renderPeriod(p, false))}
              {later.length > 0 && <div className="group-heading">Upcoming<span>{later.length}</span></div>}
              {later.map((p: any) => renderPeriod(p, true))}
            </>;
          })()}
          {!periods.length && <p className="empty">No collection periods on the calendar.</p>}
        </div>
      </TaskRail>

      <Work ref={workRef} aria-label="Selected period">
        <div className="work-heading">
          <div><h2>{selected.period_label || 'Covenants'}</h2>
            <p>Tests run on tape release and when the period closes. Each result names the clause it came from and keeps the inputs it used.</p></div>
          <StatusChip
            tone={breaches > 0 ? 'critical' : waived > 0 || atRisk > 0 ? 'warning' : 'done'} size="md"
            label={breaches > 0 ? `${breaches} in breach`
              : waived > 0 ? `${waived} waived`
                : atRisk > 0 ? `${atRisk} at risk` : covenants.length > 0 && covenants.every((c: any) => c.result?.state === 'PASS' && !c.resultIsFromAnotherPeriod) ? 'All passing' : 'Review coverage'} />
        </div>

        <Card>
          <CardHead><div><h2>Covenant results</h2>
            <p>Actual against the threshold the agreement states, with the cushion and the last four periods.</p></div></CardHead>
          <WorkspaceTools><label><input type="checkbox" checked={onlyAttention} onChange={e => setOnlyAttention(e.target.checked)} /> Needs attention only</label><span>Open a covenant to review its result, source and related controls.</span></WorkspaceTools>
          {onlyAttention && covenants.length > 0 && covenants.every((c: any) => c.result?.state === 'PASS' && !c.resultIsFromAnotherPeriod) && <Note>No covenant needs attention in this period.</Note>}
          {covenants.length === 0 ? <p style={{ color: INK.muted, fontSize: 13 }}>No covenants are defined on the activated contract.</p> : (
            <Table>
              <thead><tr>
                <th>Covenant</th><th>Agreement says</th><th className="num">Actual</th>
                <th className="num">Cushion</th><th>Trend</th><th>State</th><th></th>
              </tr></thead>
              <tbody>
                {covenants.filter((c: any) => !onlyAttention || c.result?.state !== 'PASS' || c.resultIsFromAnotherPeriod).map((c: any) => {
                  const r = c.result || {};
                  const breach = r.state === 'BREACH';
                  return <tr key={c.id} data-breach={breach}>
                    <td><strong>{c.covenant_name}</strong>
                      <small>{c.source_clause || 'clause not recorded'}{c.reporting_frequency ? ` · ${plain(c.reporting_frequency)}` : ''}{c.state === 'VERIFIED' ? ' · awaiting activation' : ''}</small></td>
                    <td>{thresholdText(c)}<small>{c.formula_expression}</small></td>
                    <td className="num">{r.actual_value == null ? '—' : valueText(c, r.actual_value)}
                      <small>{r.test_date ? date(r.test_date) : ''}{c.resultIsFromAnotherPeriod ? ' (earlier period)' : ''}</small></td>
                    <td className="num" style={{ color: breach ? '#fca5a5' : undefined }}>
                      {r.signed_cushion == null ? '—' : valueText(c, r.signed_cushion)}
                      <small>{headroomWord(c.headroomPct)}</small></td>
                    <td><Trend>{(c.trend || []).slice().reverse().map((t: any, i: number) => (
                      <i key={i} data-state={t.state} style={{ height: `${barHeight(t)}px` }}
                        title={`${t.period_label || date(t.test_date)}: ${stateWord(t.state)}, ${headroomWord(headroomOf(t)) || 'no threshold'}`} />))}</Trend></td>
                    <td><StatusChip tone={stateTone(r.state)} label={stateWord(r.state)} />
                      {r.state === 'INDETERMINATE' && <small style={{ color: INK.faint }}>{r.unavailable_reason}</small>}
                      {r.waiver_id && <small style={{ color: INK.faint }}>waiver ends {date(r.waiver_ends)}</small>}</td>
                    <td><SmallButton type="button" onClick={() => setReviewId(c.id)}>Review</SmallButton>{breach && <SmallButton type="button"
                      onClick={() => setAction(isOpen('waiver-request', c.id) ? null : { kind: 'waiver-request', id: c.id })}>
                      {isOpen('waiver-request', c.id) ? 'Close' : 'Request waiver'}</SmallButton>}</td>
                  </tr>;
                })}
              </tbody>
            </Table>
          )}
          {provenance.hasRun && <p style={{ color: INK.faint, fontSize: 12, marginTop: 10 }}>
            Tested against {contractVersionLabel(provenance.facilityVersionId, contractVersion)} on {date(provenance.testDate)},
            fired by {plain(provenance.triggeredBy)}{provenance.triggeredByRef ? ` (${provenance.triggeredByRef})` : ''}
            {provenance.createdBy ? `, recorded by ${who(provenance.createdBy)}` : ''}.
            Inputs {String(provenance.inputFingerprint || '').slice(0, 12)}… · {String(provenance.calculationVersion || '—')}
          </p>}
          {!provenance.hasRun && <p style={{ color: INK.faint, fontSize: 12, marginTop: 10 }}>
            No covenant test has run for this period yet.
          </p>}
          {covenants.filter((c: any) => isOpen('waiver-request', c.id)).map((c: any) => (
            <WaiverRequestForm key={c.id} facilityId={facilityId} covenant={c} periods={periods}
              businessDate={model.businessDate}
              onDone={closeAction} onCancel={() => setAction(null)} />
          ))}
        </Card>

        <Grid2>
          <Card>
            <CardHead><div><h2>Consequences</h2><p>What the agreement is doing to the facility right now, and since when.</p></div></CardHead>
            {consequencesActive.length === 0
              ? <p style={{ color: INK.muted, fontSize: 13 }}>Nothing is in force. A breach applies its contractual effect here.</p>
              : <Facts>{consequencesActive.map((c: any) => (
                  <React.Fragment key={c.id}>
                    <dt>{plain(c.consequence_type)}</dt>
                    <dd><strong style={{ color: '#fca5a5' }}>{c.effect_text}</strong>
                      <div style={{ color: INK.muted, fontSize: 12 }}>
                        from {c.covenant_name || 'a covenant breach'}{c.source_clause ? ` · ${c.source_clause}` : ''}</div></dd>
                  </React.Fragment>))}</Facts>}
            {(model.consequences?.all || []).filter((c: any) => c.status === 'LIFTED').length > 0 && (
              <Signoff style={{ marginTop: 12 }}>
                <summary>Lifted <small>each one names who lifted it and why</small></summary>
                <Readable><Facts>{(model.consequences.all).filter((c: any) => c.status === 'LIFTED').map((c: any) => (
                  <React.Fragment key={c.id}>
                    <dt>{plain(c.consequence_type)}</dt>
                    <dd>{c.effect_text} — lifted {date(c.lifted_at)} by {who(c.lifted_by)} ({plain(c.lifted_cause)})</dd>
                  </React.Fragment>))}</Facts></Readable>
              </Signoff>)}
          </Card>

          <Card>
            <CardHead><div><h2>Waivers</h2><p>A waiver covers a covenant for a period range and ends on a date. At expiry the consequences come back.</p></div></CardHead>
            {waivers.length === 0
              ? <p style={{ color: INK.muted, fontSize: 13 }}>No waivers on this facility.</p>
              : <Table>
                  <thead><tr><th>Covenant</th><th>Type</th><th>Scope</th><th>Ends</th><th>State</th></tr></thead>
                  <tbody>{waivers.map((w: any) => (
                    <tr key={w.id} data-pending={w.status === 'PENDING_APPROVAL'}>
                      <td>{w.covenant_name}<small>{w.requested_by} → {who(w.approved_by)}</small></td>
                      <td>{plain(w.waiver_type)}{w.amendment_version_no ? <small>amendment v{w.amendment_version_no}</small> : null}</td>
                      <td>{w.scope_from_label || '—'}{w.scope_to_label && w.scope_to_label !== w.scope_from_label ? ` – ${w.scope_to_label}` : ''}</td>
                      <td>{date(w.effective_to)}{w.daysRemaining != null && w.status === 'ACTIVE'
                        ? <small style={{ color: w.expiringSoon ? '#edcc82' : undefined }}>
                            {w.daysRemaining >= 0 ? `${w.daysRemaining} days left` : 'past its end date'}</small> : null}</td>
                      <td><StatusChip tone={w.status === 'ACTIVE' ? 'done' : w.status === 'PENDING_APPROVAL' ? 'progress' : 'neutral'} label={plain(w.status)} />
                        {w.status === 'PENDING_APPROVAL' && (mine(w.requested_by)
                          ? <small style={{ color: INK.faint, display: 'block', marginTop: 4 }}>
                              You requested this, so it needs another actor</small>
                          : <SmallButton type="button" style={{ marginTop: 4 }}
                              onClick={() => setAction(isOpen('waiver-decide', w.id) ? null : { kind: 'waiver-decide', id: w.id })}>
                              {isOpen('waiver-decide', w.id) ? 'Close' : 'Decide'}</SmallButton>)}</td>
                    </tr>))}</tbody>
                </Table>}
            {waivers.filter((w: any) => isOpen('waiver-decide', w.id)).map((w: any) => (
              <WaiverDecision key={w.id} facilityId={facilityId} waiver={w}
                onDone={closeAction} onCancel={() => setAction(null)} />
            ))}
          </Card>
        </Grid2>

        <Card style={{ marginTop: 14 }}>
          <CardHead><div><h2>Exceptions</h2>
            <p>One queue for the facility. Due dates come from the payment calendar; the SLA is computed, not typed.</p></div>
            {overdue.length > 0 && <StatusChip tone="critical" label={`${overdue.length} past due`} />}</CardHead>
          {exceptions.length === 0 ? <p style={{ color: INK.muted, fontSize: 13 }}>Nothing open.</p> : (
            <Table>
              <thead><tr><th>Item</th><th>Owner</th><th>Due</th><th>SLA</th><th>Blocking</th><th></th></tr></thead>
              <tbody>{exceptions.map((e: any) => (
                <tr key={e.id}>
                  <td><strong>{e.title}</strong><small>{plain(e.exception_type)} · {plain(e.status)}</small></td>
                  <td>{who(e.owner_username)}<small>{e.owner_assigned_by === 'SYSTEM:DEFAULT_OWNER' ? 'facility default' : who(e.owner_assigned_by)}</small></td>
                  <td>{date(e.due_at)}<small>{plain(e.due_date_basis, '')}</small></td>
                  <td><StatusChip tone={['OVERDUE', 'ESCALATED'].includes(String(e.sla_status)) ? 'critical'
                    : e.sla_status === 'DUE_SOON' ? 'warning' : e.sla_status === 'UNASSIGNED' ? 'neutral' : 'done'}
                    label={plain(e.sla_status)} /></td>
                  <td>{e.holdsFunding ? <StatusChip tone="critical" label="Holds funding" /> : '—'}</td>
                  <td>{e.status === 'PENDING_APPROVAL' && mine(e.proposed_by)
                    ? <small style={{ color: INK.faint }}>You proposed this, so it needs another actor</small>
                    : <SmallButton type="button"
                        onClick={() => setAction(isOpen('exception', e.id) ? null : { kind: 'exception', id: e.id })}>
                        {isOpen('exception', e.id) ? 'Close' : e.status === 'PENDING_APPROVAL' ? 'Review' : 'Work'}</SmallButton>}</td>
                </tr>))}</tbody>
            </Table>
          )}
          {exceptions.filter((e: any) => isOpen('exception', e.id)).map((e: any) => (
            <ExceptionActions key={e.id} facilityId={facilityId} exception={e}
              onDone={closeAction} onCancel={() => setAction(null)} />
          ))}
        </Card>

        <Signoff>
          <summary>Surveillance <small>pool performance and the thresholds that test it</small></summary>
          <Readable>
            <h3 style={{ marginTop: 0 }}>Pool performance</h3>
            {poolMetrics.note && <p style={{ color: INK.muted, fontSize: 13 }}>{poolMetrics.note}</p>}
            <Table>
              <thead><tr><th>Metric</th><th className="num">Value</th><th className="num">Change</th><th className="num">Threshold</th><th>Tested</th></tr></thead>
              <tbody>{(poolMetrics.lines || []).map((m: any) => (
                <tr key={m.metricName}>
                  <td>{m.label}{m.note ? <small>{m.note}</small> : null}</td>
                  <td className="num">{m.value == null ? '—' : `${num(m.value)}%`}
                    {m.valueSource === 'SURVEILLANCE_TRIGGER'
                      ? <small>from the tape, {date(m.valueAsOf)}</small> : null}</td>
                  <td className="num">{m.deltaPpt == null
                    ? <span style={{ color: INK.faint }}>{m.hasPriorReleasedTape ? '—' : 'first observation'}</span>
                    : `${Number(m.deltaPpt) >= 0 ? '+' : ''}${num(m.deltaPpt)} pts`}</td>
                  <td className="num">{m.threshold == null ? '—' : `${num(m.threshold)}%`}</td>
                  <td>{m.governed
                    ? <StatusChip tone="done" label={plain(m.severity, 'Governed')} />
                    : <StatusChip tone="neutral" label="No threshold" />}
                    <SmallButton type="button" style={{ marginLeft: 6 }}
                      onClick={() => toggleThresholdForm(m.metricName)}>
                      {m.governed ? 'Change' : 'Set one'}</SmallButton></td>
                </tr>))}</tbody>
            </Table>
            <h3 style={{ marginTop: 16 }}>Triggers</h3>
            {(model.triggers || []).length === 0 ? <p style={{ color: INK.muted }}>No triggers on this facility.</p> : (
              <Table>
                <thead><tr><th>As of</th><th>Metric</th><th className="num">Value</th><th className="num">Threshold</th><th>Source</th><th>State</th></tr></thead>
                <tbody>{model.triggers.map((t: any) => (
                  <tr key={t.id}>
                    <td>{date(t.as_of_date)}</td>
                    <td>{plain(t.metric_name)}<small>{t.message}</small></td>
                    <td className="num">{num(t.current_value)}</td>
                    <td className="num">{num(t.threshold_value)}</td>
                    <td>{plain(t.metric_source)}{t.coverage_pct != null ? <small>coverage {num(t.coverage_pct)}%</small> : null}</td>
                    <td><StatusChip tone={t.status === 'OPEN' ? 'warning' : 'neutral'} label={plain(t.status)} /></td>
                  </tr>))}</tbody>
              </Table>)}
            <h3 style={{ marginTop: 16 }}>Thresholds</h3>
            <Table>
              <thead><tr><th>Metric</th><th className="num">Level</th><th className="num">Delta</th><th>Severity</th><th>Scope</th><th>State</th></tr></thead>
              <tbody>{(model.thresholds || []).map((t: any) => (
                <tr key={t.id}>
                  <td>{plain(t.metric_name)}{t.rationale ? <small>{t.rationale}</small> : null}</td>
                  <td className="num">{num(t.level_threshold)}{t.previous_level != null ? <small>was {num(t.previous_level)}</small> : null}</td>
                  <td className="num">{t.delta_threshold_ppt == null ? '—' : num(t.delta_threshold_ppt)}</td>
                  <td>{plain(t.severity)}</td>
                  <td>{plain(t.scope)}</td>
                  <td><StatusChip tone={t.state === 'ACTIVE' ? 'done' : 'progress'} label={plain(t.state)} />
                    {t.approved_by ? <small>{t.proposed_by} → {t.approved_by}</small> : t.proposed_by ? <small>proposed by {t.proposed_by}</small> : null}
                    {t.state === 'PENDING_APPROVAL' && (mine(t.proposed_by)
                      ? <small style={{ color: INK.faint, display: 'block', marginTop: 4 }}>
                          You proposed this, so it needs another actor</small>
                      : <SmallButton type="button" style={{ marginTop: 4 }}
                          onClick={() => setAction(isOpen('threshold-decide', t.id) ? null : { kind: 'threshold-decide', id: t.id })}>
                          {isOpen('threshold-decide', t.id) ? 'Close' : 'Decide'}</SmallButton>)}</td>
                </tr>))}</tbody>
            </Table>
            {action?.kind === 'threshold-new' && <ThresholdForm facilityId={facilityId}
              metricName={thresholdMetric} onDone={closeAction} onCancel={() => setAction(null)} />}
            {(model.thresholds || []).filter((t: any) => isOpen('threshold-decide', t.id)).map((t: any) => (
              <ThresholdDecision key={t.id} facilityId={facilityId} threshold={t}
                onDone={closeAction} onCancel={() => setAction(null)} />
            ))}
          </Readable>
        </Signoff>

        <Signoff>
          <summary>Compliance certificates <small>the borrower's covenant figures, against what we computed · the borrowing base certificate is on Collateral</small></summary>
          <Readable>
            {(model.certificates || []).length === 0
              ? <p style={{ color: INK.muted }}>No compliance certificate recorded. This is the officer's covenant certificate, not the borrowing base certificate. Upload one in Document Processing, or key it here.</p>
              : (model.certificates || []).map((c: any) => (
                  <div key={c.id} style={{ marginBottom: 14, opacity: c.status === 'SUPERSEDED' ? 0.55 : 1 }}>
                    <strong>{date(c.as_of_date)}</strong> · {plain(c.entry_channel)} · {c.matched_count} matched, {c.differed_count} differ, {c.reported_only_count} reported only
                    {c.status === 'SUPERSEDED' && <StatusChip tone="neutral" label={c.superseded_by_id ? `Restated by #${c.superseded_by_id}` : 'Superseded'} />}
                    <Table style={{ marginTop: 8 }}>
                      <thead><tr><th>Covenant</th><th>Unit</th><th className="num">Borrower says</th><th className="num">We compute</th><th>Citation</th><th>Status</th></tr></thead>
                      <tbody>{((c.lines_json || {}).lines || []).map((l: CertificateLine, i: number) => (
                        <tr key={i}>
                          <td>{l.covenantName || l.covenantKey}</td>
                          <td style={{ color: INK.muted }}>{certificateUnitWord(certificateLineUnit(l), currency)}</td>
                          <td className="num">{certificateValueText(l, 'reported', currency)}</td>
                          <td className="num">{certificateValueText(l, 'computed', currency)}</td>
                          <td>{l.citation || 'No citation'}</td>
                          <td><StatusChip tone={l.status === 'MATCHED' ? 'done' : l.status === 'DIFFERENT' ? 'warning' : 'neutral'} label={plain(l.status)} /></td>
                        </tr>))}</tbody>
                    </Table>
                  </div>))}
            <SmallButton type="button" style={{ marginTop: 10 }}
              onClick={() => setAction(action?.kind === 'certificate' ? null : { kind: 'certificate' })}>
              {action?.kind === 'certificate' ? 'Close' : 'Record a certificate'}</SmallButton>
            {action?.kind === 'certificate' && <CertificateForm facilityId={facilityId} covenants={covenants}
              asOfDefault={selected.period_end ? String(selected.period_end) : undefined}
              onDone={closeAction} onCancel={() => setAction(null)} />}
          </Readable>
        </Signoff>

        <Signoff onToggle={e => setRetirementOpen((e.currentTarget as HTMLDetailsElement).open)}>
          <summary>Monitoring engine coverage <small>estate-wide check that every facility is tested by the governed engine</small></summary>
          <Readable>
            <LegacyRetirementReport open={retirementOpen} />
          </Readable>
        </Signoff>

        <Bar>
          <div className="state">
            {model.coverage?.monitored
              ? <>Tests run on tape release and at period close. Last tested <strong>{provenance.testDate ? date(provenance.testDate) : 'never'}</strong>.
                  {' '}Pool evidence {poolMetrics.hasRun ? <>frozen {date(poolMetrics.asOfDate)}</> : <>not frozen yet</>}.</>
              : <>Nothing is testing this facility.</>}
          </div>
          <div className="actions">
            <Button type="button" $variant="ghost" disabled={busy !== null}
              onClick={() => void run('run', () => axios.post(`${ROOT}/facilities/${facilityId}/covenants/runs`,
                { testDate: selected.period_end, sourceReference: `Checker re-run of ${selected.period_label}` }),
                `Covenants re-tested for ${selected.period_label}.`)}>
              {busy === 'run' ? 'Testing…' : 'Re-test this period'}
            </Button>
            <Button type="button" $variant="ghost" disabled={busy !== null}
              onClick={() => void run('expire', () => axios.post(`${ROOT}/facilities/${facilityId}/waivers/expire`, {}),
                'Waivers past their end date are expired and their consequences restored.')}>
              {busy === 'expire' ? 'Checking…' : 'Expire due waivers'}
            </Button>
            <Button type="button" $variant="ghost" disabled={busy !== null}
              onClick={() => void run('evidence', () => refreshMonitoringEvidence(facilityId, String(selected.period_end || model.businessDate)),
                'Monitoring evidence was refreshed; no economic authority changed.')}>
              {busy === 'evidence' ? 'Freezing…' : 'Refresh monitoring evidence'}
            </Button>
          </div>
        </Bar>
      </Work>
    </Layout>
  </Shell>;
};

export default CovenantMonitoringPanel;
